/**
 * Where a single-Location ("property / site / building") target is offered for alert
 * rules, budgets, bill statements and digest subscriptions.
 *
 * The backend accepts a Location scope for every flavour; which flavours SEE it is a
 * frontend decision. School is deliberately absent (its Locations are boarding houses and
 * the live school screens stay unchanged), as is home (a single dwelling). The label comes
 * from the `scopeLocation` term in lib/terminology.js.
 */

export const LOCATION_SCOPE_FLAVOURS = ['estate', 'business', 'university']

/**
 * Whether the Location target should be offered: the flavour is in the location-scope set
 * and the org has at least one Location to pick.
 */
export function locationScopeAvailable(flavour, locationCount) {
    return (
        LOCATION_SCOPE_FLAVOURS.includes(flavour) && Number(locationCount) > 0
    )
}

// ---------------------------------------------------------------------------------------
// Location members (location-scoped-member-role). The me payload's `locationAccess` is
// `null` for an unrestricted user and `{locations: [...], locationIds: [...]}` for a
// location member. The API is the enforcing half; these helpers only shape the UI.
// ---------------------------------------------------------------------------------------

/** Whether `user` is location-scoped (their `locationAccess` is set). */
export function isLocationScoped(user) {
    return Boolean(user?.locationAccess)
}

/** The ids of every Location the user may see, or `null` when unrestricted. */
export function scopedLocationIds(user) {
    if (!isLocationScoped(user)) return null
    return (user.locationAccess.locationIds ?? []).map(String)
}

/** The user's granted Locations (`{id, name, ...}`), or `[]` when unrestricted. */
export function scopedLocations(user) {
    if (!isLocationScoped(user)) return []
    return user.locationAccess.locations ?? []
}

/** Whether `user` may see Location `id` (always true when unrestricted). */
export function canSeeLocation(user, id) {
    const ids = scopedLocationIds(user)
    if (ids === null) return true
    if (id === null || id === undefined || id === '') return false
    return ids.includes(String(id))
}

/**
 * The navigation/route rule mirroring the backend gate: for a location-scoped user only
 * entries marked `locationScopeAware` are reachable. Everyone else is unaffected.
 */
export function locationScopeAllowsEntry(entry, user) {
    if (!isLocationScoped(user)) return true
    return entry?.locationScopeAware === true
}

/** Peek types a location member may ever open, and only for in-scope ids. */
export const LOCATION_MEMBER_PEEK_TYPES = [
    'location',
    'electricity-meter',
    'gas-meter',
    'water-meter'
]

/**
 * Whether a location member may open a `type` peek for this reference. A Location peek
 * needs its `id` in scope; a meter peek needs the meter's `locationId` in scope (a meter
 * whose Location the page does not know renders as plain text). Organisation, org-summary,
 * user, group and partner peeks are never available.
 */
export function locationMemberMayPeek(user, type, { id, locationId } = {}) {
    if (!LOCATION_MEMBER_PEEK_TYPES.includes(type)) return false
    if (type === 'location') return canSeeLocation(user, id)
    return locationId !== undefined && canSeeLocation(user, locationId)
}

/**
 * The confirmation an admin sees before a re-parent that changes location access, from
 * the API's dry-run `accessImpact` (`membersGaining`, `membersLosing`, `membersAffected`).
 * `null` when nobody's access changes, so the move saves without a prompt.
 */
export function reparentImpactMessage(impact) {
    const affected = impact?.membersAffected ?? 0
    if (!affected) return null
    const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`
    const parts = []
    if (impact.membersGaining) {
        parts.push(`${people(impact.membersGaining)} will gain access to it`)
    }
    if (impact.membersLosing) {
        parts.push(
            `${people(impact.membersLosing)} will lose access to part of it`
        )
    }
    return `Moving this location changes who can see it: ${parts.join(' and ')}. Save anyway?`
}

/**
 * The people a re-parent affects, as list rows for the confirmation dialog, from the
 * dry-run `accessImpact.members` (`id`, `name`, `email`, `gained`, `lost` as counts of
 * locations). A member with no name falls back to their email, then to a generic label.
 * Empty when the API sent no member list.
 */
export function reparentImpactMembers(impact) {
    const places = (n) => `${n} ${n === 1 ? 'location' : 'locations'}`
    return (impact?.members ?? []).map((member) => {
        const changes = []
        if (member.gained)
            changes.push(`gains access to ${places(member.gained)}`)
        if (member.lost) changes.push(`loses access to ${places(member.lost)}`)
        return {
            id: member.id,
            label: member.name || member.email || 'Unnamed member',
            email: member.name ? member.email || '' : '',
            change: changes.join(' and ')
        }
    })
}
